import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * Drag/swipe state
   */
  #dragStartX = null;
  #dragCurrentX = null;
  #isDragging = false;

  /**
   * Document-level drag handlers (bound for removal)
   */
  #handleMove = null;
  #handleEnd = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    // Initialize slides container width and setup handlers
    this.#initializeComponent();
  }

  /**
   * Initializes the component once refs are ready
   */
  #initializeComponent() {
    // Check if refs are populated (they should be after super.connectedCallback())
    if (this.refs.slides?.length > 0) {
      this.#setupComponent();
      return;
    }

    // If refs aren't ready yet, wait for next frame
    // This handles cases where DOM elements haven't been parsed yet
    requestAnimationFrame(() => {
      // Double-check we're still connected before proceeding
      if (!this.isConnected) return;

      // If refs still aren't ready, try one more frame
      if (!this.refs.slides?.length) {
        requestAnimationFrame(() => {
          if (!this.isConnected) return;
          this.#setupComponent();
        });
        return;
      }

      this.#setupComponent();
    });
  }

  /**
   * Sets up the component once refs are guaranteed to be ready
   */
  #setupComponent() {
    // Initialize slides container width
    const slidesContainer = this.querySelector('.announcement-bar__slides');
    if (slidesContainer && this.refs.slides?.length) {
      const slideCount = this.refs.slides.length;
      slidesContainer.style.width = `${slideCount * 100}%`;
    }

    // Add click handlers for pagination dots
    const paginationDots = this.querySelectorAll('.announcement-bar__pagination-dot');
    paginationDots?.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.current = index;
        this.pause();
        this.play();
      });
    });

    // Add drag/swipe handlers
    this.#setupDragHandlers();

    // Start autoplay
    this.play();
  }

  /**
   * Setup drag and swipe handlers for touch and mouse
   */
  #setupDragHandlers() {
    const slidesContainer = this.querySelector('.announcement-bar__slides');
    if (!slidesContainer) return;

    const handleStart = (e) => {
      // Prevent link navigation if this becomes a drag
      const link = e.target.closest('a');
      if (link) {
        link.dataset.preventClick = 'false';
      }
      
      this.#isDragging = true;
      this.#dragStartX = this.#getPointerX(e);
      this.#dragCurrentX = this.#dragStartX;
      slidesContainer.style.transition = 'none';
      this.suspend();

      // Add document-level handlers when drag starts
      this.#handleMove = (moveEvent) => {
        if (!this.#isDragging) return;
        
        // If we've moved enough, prevent link click
        const deltaX = Math.abs(this.#getPointerX(moveEvent) - this.#dragStartX);
        if (deltaX > 5) {
          const link = moveEvent.target.closest('a');
          if (link) {
            link.dataset.preventClick = 'true';
          }
        }
        
        this.#dragCurrentX = this.#getPointerX(moveEvent);
        const moveDeltaX = this.#dragCurrentX - this.#dragStartX;
        
        // Use cached slidesContainer reference
        if (!slidesContainer || !this.refs.slides?.length) return;
        
        const slideCount = this.refs.slides.length;
        const sliderWidth = slidesContainer.parentElement.offsetWidth;
        const slideWidth = sliderWidth;
        // Use normalized current index for drag calculations
        const currentNormalizedIndex = this.#current;
        const currentTranslate = -currentNormalizedIndex * slideWidth;
        const newTranslate = currentTranslate + moveDeltaX;
        
        // Constrain dragging to slide boundaries
        const minTranslate = -(slideCount - 1) * slideWidth;
        const maxTranslate = 0;
        const constrainedTranslate = Math.max(minTranslate, Math.min(maxTranslate, newTranslate));
        
        slidesContainer.style.translate = `${constrainedTranslate}px 0`;
      };

      this.#handleEnd = (endEvent) => {
        if (!this.#isDragging) return;
        
        // Use cached slidesContainer reference
        if (!slidesContainer) return;
        
        const deltaX = this.#dragCurrentX - this.#dragStartX;
        const threshold = 50; // Minimum drag distance to trigger slide change
        const wasDrag = Math.abs(deltaX) > threshold;
        
        // Stop dragging first
        this.#isDragging = false;
        
        // Remove document-level handlers
        this.#removeDocumentHandlers();
        
        // Restore transition
        slidesContainer.style.transition = '';
        
        if (wasDrag) {
          // Prevent link navigation if it was a drag
          const link = endEvent?.target?.closest('a');
          if (link) {
            link.dataset.preventClick = 'true';
          }
          
          if (deltaX > 0) {
            // Dragged right, go to previous slide
            this.previous();
          } else {
            // Dragged left, go to next slide
            this.next();
          }
        } else {
          // Not enough drag, snap back to current slide
          this.#updateSlidePosition();
        }
        
        this.#dragStartX = null;
        this.#dragCurrentX = null;
        this.resume();
      };

      // Add document-level handlers
      document.addEventListener('mousemove', this.#handleMove);
      document.addEventListener('mouseup', this.#handleEnd);
      document.addEventListener('touchmove', this.#handleMove, { passive: true });
      document.addEventListener('touchend', this.#handleEnd);
    };

    // Use event delegation - attach handlers only to container
    // Events will bubble up from child elements (slides, text, links)
    slidesContainer.addEventListener('mousedown', handleStart);
    slidesContainer.addEventListener('touchstart', handleStart, { passive: true });
    
    // Use event delegation for click handler to prevent link navigation after drag
    slidesContainer.addEventListener('click', (e) => {
      const link = e.target.closest('a');
      if (link && link.dataset.preventClick === 'true') {
        e.preventDefault();
        delete link.dataset.preventClick;
      }
    });
  }

  /**
   * Remove document-level drag handlers
   */
  #removeDocumentHandlers() {
    if (this.#handleMove) {
      document.removeEventListener('mousemove', this.#handleMove);
      document.removeEventListener('touchmove', this.#handleMove);
      this.#handleMove = null;
    }
    if (this.#handleEnd) {
      document.removeEventListener('mouseup', this.#handleEnd);
      document.removeEventListener('touchend', this.#handleEnd);
      this.#handleEnd = null;
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    
    // Clean up document-level handlers if component is removed
    this.#removeDocumentHandlers();
    
    // Clean up visibility change listener
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  /**
   * Get pointer X coordinate from event (mouse or touch)
   */
  #getPointerX(e) {
    return e.touches ? e.touches[0].clientX : e.clientX;
  }

  next() {
    const slideCount = this.refs.slides?.length ?? 0;
    if (slideCount === 0) return;
    
    // Simple forward increment with modulo for looping
    this.#current = (this.#current + 1) % slideCount;
    this.#updateSlidePosition();
  }

  previous() {
    const slideCount = this.refs.slides?.length ?? 0;
    if (slideCount === 0) return;
    
    // Simple backward decrement with modulo for looping
    this.#current = (this.#current - 1 + slideCount) % slideCount;
    this.#updateSlidePosition();
  }

  /**
   * Updates the slide position and UI state
   */
  #updateSlidePosition() {
    const slideCount = this.refs.slides?.length ?? 0;
    if (slideCount === 0) return;

    const slidesContainer = this.querySelector('.announcement-bar__slides');
    if (!slidesContainer) return;

    // Update translate position
    if (!this.#isDragging) {
      const translateX = -this.#current * 100;
      slidesContainer.style.translate = `${translateX}% 0`;
    }

    // Update aria-hidden attributes
    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== this.#current}`);
    });

    // Update pagination dots (visual indicators only, not interactive)
    const paginationDots = this.querySelectorAll('.announcement-bar__pagination-dot');
    paginationDots?.forEach((dot, index) => {
      const isActive = index === this.#current;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-selected', `${isActive}`);
    });
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;
    
    const slideCount = this.refs.slides?.length ?? 0;
    if (slideCount === 0 || slideCount === 1) return; // Don't autoplay if 0 or 1 slide

    this.paused = false;
    this.suspend(); // Clear any existing interval

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    const slideCount = this.refs.slides?.length ?? 0;
    if (slideCount === 0) return;

    // Normalize the index to always be within bounds (0 to slideCount - 1)
    let normalizedIndex = current % slideCount;
    if (normalizedIndex < 0) {
      normalizedIndex += slideCount;
    }
    
    // Store the normalized index
    this.#current = normalizedIndex;
    this.#updateSlidePosition();
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
